import * as React from 'react';
import CheckBox from '@roo/roo/CheckBox';

const { useContext, useRef, createContext, useCallback, useEffect, useState } = React;

interface CtxData {
    defaultSelected: string[];
    dataRef?: React.MutableRefObject<(string | number)[]>;
    selectAllRef?: React.MutableRefObject<(checked: boolean, indeterminate: boolean) => void>;
    allSelected: number[];
}

interface IProps {
    /**
     * 可用来获取内部选项值
     */
    handle: CustomRef;
    /**
     * 此值变化会引起全部SelectItem子组件渲染
     * 通常各个SelectItem的点击事件不会引起全局渲染，如需获取所有选项值，请使用handle属性
     */
    defaultSelected?: string[];

    /**
     * 全量选择项，用于全选按钮
     */
    allSelected: number[];
}

export const SelectContext = createContext<CtxData>({
    defaultSelected: [],
    allSelected: [],
});

export interface CustomRef {
    getSelected?: () => string[];
}

interface SelectItemProps {
    rowKey: string | number;
}

const SelectGroup: React.FC<IProps> = ({ handle, defaultSelected = [], allSelected, children }) => {
    const selectedDataRef = useRef<string[]>([]);
    // eslint-disable-next-line max-len
    const selectAllRef = useRef<(checked: boolean, indeterminate: boolean) => void>(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (_checked: boolean, _indeterminate: boolean) => {},
    );
    // eslint-disable-next-line no-param-reassign
    handle.getSelected = useCallback(() => selectedDataRef.current, []);
    useEffect(() => {
        selectedDataRef.current = defaultSelected;
        if (selectAllRef) {
            if (defaultSelected.length === allSelected.length) {
                selectAllRef.current(true, false);
            } else if (defaultSelected.length === 0) {
                selectAllRef.current(false, false);
            } else {
                selectAllRef.current(false, true);
            }
        }
    }, [allSelected.length, defaultSelected]);
    return (
        <SelectContext.Provider
            value={{
                defaultSelected,
                dataRef: selectedDataRef,
                selectAllRef,
                allSelected,
            }}
        >
            {children}
        </SelectContext.Provider>
    );
};

export const useCheckGroup = () => useContext(SelectContext);

export const SelectItem: React.FC<SelectItemProps> = ({ rowKey }) => {
    const context = useCheckGroup();
    const lastDefaultCheckedRef = useRef<boolean>(true);
    const [checked, setChecked] = useState<boolean>(false);

    React.useEffect(() => {
        let val: boolean = false;
        if (context.defaultSelected.findIndex(_key => _key === rowKey) !== -1) {
            val = true;
        }
        const isDefaultDiff: boolean = lastDefaultCheckedRef.current !== val;
        if (isDefaultDiff && val !== checked) {
            setChecked(val);
        }
        lastDefaultCheckedRef.current = val;
        // 注意此处不加 'checked', 原因是，在正常内部点击的时候，并不需要判断根据defaultSelected值。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.defaultSelected, rowKey, checked]);

    const handleChange = e => {
        const { dataRef, selectAllRef, allSelected } = context;
        if (dataRef) {
            const index = dataRef.current.findIndex(item => item === rowKey);
            setChecked(e.target.checked);
            if (index === -1 && e.target.checked) {
                dataRef.current.push(rowKey);
            } else if (index !== -1 && !e.target.checked) {
                dataRef.current.splice(index, 1);
            }
        }
        if (selectAllRef && dataRef) {
            if (dataRef.current.length === allSelected.length) {
                selectAllRef.current(true, false);
            } else if (dataRef.current.length === 0) {
                selectAllRef.current(false, false);
            } else {
                selectAllRef.current(false, true);
            }
        }
    };
    return <CheckBox checked={checked} onChange={handleChange} />;
};

interface SelectAllProps {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * 选择全部组件
 */
export const SelectAll: React.FC<SelectAllProps> = ({ onChange }) => {
    const context = useCheckGroup();
    const [indeterminate, setIndeterminate] = useState<boolean>(false);
    const [checked, setChecked] = useState<boolean>(false);
    const setSelectAll = useCallback((checked: boolean, indeterminate: boolean) => {
        setChecked(checked);
        setIndeterminate(indeterminate);
    }, []);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(e.target.checked);
        setIndeterminate(false);
        onChange(e);
    };
    useEffect(() => {
        if (context.selectAllRef) {
            context.selectAllRef.current = setSelectAll;
        }
    }, [context.selectAllRef, setSelectAll]);
    return <CheckBox checked={checked} indeterminate={indeterminate} onChange={handleChange} />;
};

export default SelectGroup;
